/**
 * Diagnostics — "Why did this time out?" operator delegation trace view (ADR-051 D1).
 *
 * List view: recent delegations filterable by outcome (timeouts+errors default, all toggle).
 * Each row shows profile / outcome / duration / started_at. Click a row to drill
 * into the per-call attribution trace.
 *
 * Trace view: calls ordered by seq, longest calls visually highlighted, error
 * badges, rollup (total calls / total latency / longest call).
 *
 * RBAC: operator|fleet_owner only (viewer → access-denied panel).
 */
import { useEffect, useState, useCallback } from 'react'
import { api, ForbiddenError } from '../api/client'
import { CapturePanel } from './CapturePanel'
import type { DelegationEvent, DelegationCall, DelegationTraceResponse, DelegationsParams, ResourceWindow } from '../api/client'
import { SpanTree } from '../components/SpanTree'
import { TelemetryBanner } from '../components/TelemetryBanner'
import { formatMs } from '../utils/formatMs'
import { relativeTime } from '../utils/relativeTime'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function outcomeClass(outcome: string): string {
  switch (outcome) {
    case 'success': return 'bg-green-100 text-green-800'
    case 'timeout': return 'bg-red-100 text-red-800'
    case 'error':   return 'bg-orange-100 text-orange-800'
    case 'empty':   return 'bg-gray-100 text-gray-600'
    default:        return 'bg-gray-100 text-gray-600'
  }
}

function kindClass(kind: string | null): string {
  switch (kind) {
    case 'model':   return 'bg-indigo-100 text-indigo-800'
    case 'tool':    return 'bg-blue-100 text-blue-800'
    case 'mcp':     return 'bg-purple-100 text-purple-800'
    default:        return 'bg-gray-100 text-gray-600'
  }
}

function fmtDuration(s: number | null | undefined): string {
  if (s == null) return '—'
  if (s < 1) return `${(s * 1000).toFixed(0)}ms`
  return `${s.toFixed(1)}s`
}

// ---------------------------------------------------------------------------
// CallRow — a single per-call attribution row in the trace timeline
// ---------------------------------------------------------------------------

interface CallRowProps {
  call: DelegationCall
  isLongest: boolean
  maxLatency: number
}

function CallRow({ call, isLongest, maxLatency }: CallRowProps) {
  const [expanded, setExpanded] = useState(false)
  const pct = maxLatency > 0 ? Math.round(((call.latency_ms ?? 0) / maxLatency) * 100) : 0

  return (
    <li className={`border-b border-gray-100 last:border-0 ${isLongest ? 'bg-amber-50' : ''}`}>
      <div
        className="px-3 py-1.5 flex items-center gap-2 text-xs cursor-pointer hover:brightness-95 select-none"
        onClick={() => setExpanded(e => !e)}
      >
        {/* seq */}
        <span className="shrink-0 text-gray-300 w-6 text-right font-mono">{call.seq ?? '—'}</span>

        {/* kind chip */}
        <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium w-14 justify-center ${kindClass(call.kind)}`}>
          {call.kind ?? '—'}
        </span>

        {/* target */}
        <span className="flex-1 font-mono text-gray-800 truncate" title={call.target ?? ''}>
          {call.target ?? '—'}
        </span>

        {/* latency bar */}
        <div className="shrink-0 flex items-center gap-1.5 w-36">
          <div className="h-1.5 bg-gray-100 rounded-full flex-1">
            <div
              className={`h-1.5 rounded-full ${isLongest ? 'bg-amber-500' : 'bg-indigo-400'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className={`font-mono w-16 text-right ${isLongest ? 'text-amber-700 font-semibold' : 'text-gray-600'}`}>
            {formatMs(call.latency_ms)}
            {isLongest && <span className="ml-1 text-[10px] text-amber-500">peak</span>}
          </span>
        </div>

        {/* outcome badge */}
        {call.outcome && call.outcome !== 'success' && (
          <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${outcomeClass(call.outcome)}`}>
            {call.outcome}
          </span>
        )}

        {/* error_class badge */}
        {call.error_class && (
          <span className="shrink-0 text-[10px] font-mono text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
            {call.error_class}
          </span>
        )}

        {/* expand indicator */}
        <span className="shrink-0 text-gray-300 w-3">{expanded ? '▾' : '▸'}</span>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-1 bg-gray-50 border-t border-gray-100 text-xs text-gray-600 font-mono space-y-1 select-text">
          {call.tokens_in != null && (
            <div>tokens_in: {call.tokens_in} / tokens_out: {call.tokens_out}</div>
          )}
          {call.http_code != null && <div>http_code: {call.http_code}</div>}
          {call.retries != null && call.retries > 0 && (
            <div>retries: {call.retries} (backoff: {call.backoff_ms}ms)</div>
          )}
          {call.detail && (
            <pre className="whitespace-pre-wrap break-all bg-white border border-gray-200 rounded p-2 max-h-48 overflow-auto">
              {call.detail}
            </pre>
          )}
        </div>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// ResourceWindowPanel — box resource context during a delegation (ADR-051 D3)
// ---------------------------------------------------------------------------

interface ResourceWindowPanelProps {
  resourceWindow: ResourceWindow | null
}

function ResourceWindowPanel({ resourceWindow }: ResourceWindowPanelProps) {
  if (!resourceWindow || !resourceWindow.rollup) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Box Resources</h3>
        <p className="text-xs text-gray-400">No resource samples in this delegation window.</p>
      </div>
    )
  }

  const { rollup } = resourceWindow
  const loadPerCore = rollup.load_per_core
  const isElevatedLoad = rollup.elevated_load === true
  const isElevatedMem = rollup.elevated_mem

  return (
    <div className={`rounded-lg border px-4 py-3 ${isElevatedLoad || isElevatedMem ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Box Resources</h3>
        {(isElevatedLoad || isElevatedMem) && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
            elevated
          </span>
        )}
        <span className="text-[10px] text-gray-400 ml-auto">{rollup.sample_count} sample{rollup.sample_count !== 1 ? 's' : ''}</span>
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-1 text-xs text-gray-600">
        {/* CPU load */}
        <span>
          <span className="font-semibold text-gray-700">load1 (max):</span>{' '}
          <span className={isElevatedLoad ? 'text-amber-700 font-semibold' : ''}>
            {rollup.max_load1.toFixed(2)}
          </span>
          {rollup.ncpu != null && (
            <span className="text-gray-400"> / {rollup.ncpu} cores</span>
          )}
          {loadPerCore != null && (
            <span className={`ml-1 text-[10px] ${isElevatedLoad ? 'text-amber-600' : 'text-gray-400'}`}>
              ({loadPerCore.toFixed(2)}×)
              {isElevatedLoad && <span className="ml-0.5 text-amber-500">⚠</span>}
            </span>
          )}
        </span>

        <span>
          <span className="font-semibold text-gray-700">load1 (avg):</span>{' '}
          {rollup.avg_load1.toFixed(2)}
        </span>

        {/* Memory */}
        <span>
          <span className="font-semibold text-gray-700">mem used (max):</span>{' '}
          <span className={isElevatedMem ? 'text-amber-700 font-semibold' : ''}>
            {rollup.max_mem_used_pct.toFixed(1)}%
            {isElevatedMem && <span className="ml-1 text-[10px] text-amber-500">⚠</span>}
          </span>
        </span>
      </div>

      {(isElevatedLoad || isElevatedMem) && (
        <p className="mt-2 text-[10px] text-amber-700">
          Box was resource-elevated during this delegation.
          {isElevatedLoad && ` Load exceeded available cores (${(loadPerCore ?? 0).toFixed(2)}×).`}
          {isElevatedMem && ` Memory pressure at ${rollup.max_mem_used_pct.toFixed(1)}%.`}
          {' '}See ADR-051 D5 for the cause→remedy rubric.
        </p>
      )}
    </div>
  )
}

// TracePanel — the per-delegation trace drill-down
// ---------------------------------------------------------------------------

interface TracePanelProps {
  delegation: DelegationEvent
  onBack: () => void
}

function TracePanel({ delegation, onBack }: TracePanelProps) {
  const [trace, setTrace] = useState<DelegationTraceResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    const params: { turn_id?: string; delegation_id?: string } = {}
    if (delegation.delegation_id) params.delegation_id = delegation.delegation_id
    else if (delegation.turn_id) params.turn_id = delegation.turn_id
    if (!params.turn_id && !params.delegation_id) {
      setError('No turn_id or delegation_id available for this delegation row.')
      setLoading(false)
      return
    }
    api.getDelegationTrace(params)
      // Defensive: normalize so a partial/error body (e.g. a stale data-plane)
      // can never crash the panel with "undefined.map/.entries".
      .then(t => setTrace({
        fleet_id: t?.fleet_id ?? null,
        filter: t?.filter ?? { turn_id: params.turn_id ?? null, delegation_id: params.delegation_id ?? null },
        delegations: Array.isArray(t?.delegations) ? t.delegations : [],
        calls: Array.isArray(t?.calls) ? t.calls : [],
        rollup: t?.rollup ?? { call_count: 0, total_latency_ms: 0, longest_call: null, outcomes: {} },
        gateway_join: t?.gateway_join ?? '',
        resource_window: t?.resource_window ?? null,
        spans: Array.isArray(t?.spans) ? t.spans : undefined,
        telemetry: t?.telemetry,
      }))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load trace.'))
      .finally(() => setLoading(false))
  }, [delegation])

  const maxLatency = trace
    ? Math.max(...trace.calls.map(c => c.latency_ms ?? 0), 1)
    : 1

  const longestCallIdx = trace?.calls.reduce<number | null>(
    (maxIdx, c, idx) =>
      maxIdx === null || (c.latency_ms ?? 0) > (trace.calls[maxIdx]?.latency_ms ?? 0)
        ? idx
        : maxIdx,
    null,
  ) ?? null

  return (
    <div className="space-y-4">
      {/* Back + heading */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
        >
          ← Back
        </button>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Call trace — {delegation.profile}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {delegation.outcome} · {fmtDuration(delegation.duration_s)} · {new Date(delegation.created_at).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Delegation metadata */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 text-xs font-mono text-gray-600 space-y-1">
        {delegation.turn_id && (
          <div><span className="font-semibold text-gray-700">turn_id:</span> {delegation.turn_id}</div>
        )}
        {delegation.delegation_id && (
          <div><span className="font-semibold text-gray-700">delegation_id:</span> {delegation.delegation_id}</div>
        )}
        {delegation.caller && (
          <div><span className="font-semibold text-gray-700">caller:</span> {delegation.caller}</div>
        )}
        {delegation.query && (
          <div>
            <span className="font-semibold text-gray-700">query:</span>{' '}
            <span className="text-gray-500">
              {delegation.query.slice(0, 200)}{delegation.query.length > 200 ? '…' : ''}
            </span>
          </div>
        )}
      </div>

      {loading && <div className="text-sm text-gray-400 animate-pulse">Loading trace…</div>}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {trace && (
        <>
          {trace.telemetry === 'unavailable' && (
            <TelemetryBanner message="Span data is temporarily unavailable; legacy call data may still be shown." />
          )}

          {/* Rollup */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Rollup</h3>
            <div className="flex flex-wrap gap-x-8 gap-y-1 text-xs text-gray-600">
              <span>
                <span className="font-semibold text-gray-700">calls:</span> {trace.rollup.call_count}
              </span>
              <span>
                <span className="font-semibold text-gray-700">total latency:</span> {formatMs(trace.rollup.total_latency_ms)}
              </span>
              {trace.spans === undefined && trace.rollup.longest_call && (
                <span>
                  <span className="font-semibold text-amber-700">longest:</span>{' '}
                  <span className="font-mono">{trace.rollup.longest_call.target}</span>{' '}
                  ({trace.rollup.longest_call.kind}) — {formatMs(trace.rollup.longest_call.latency_ms)}
                </span>
              )}
              {Object.entries(trace.rollup.outcomes).map(([k, v]) => (
                <span key={k}>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${outcomeClass(k)}`}>
                    {k}
                  </span>
                  {' '}{v}
                </span>
              ))}
            </div>
            {trace.gateway_join === 'deferred' && (
              <p className="text-[10px] text-gray-300 mt-2">
                {/* TODO(ADR-050 P1): gateway ledger join (hs_api_calls / analytics api_calls) deferred —
                    query hs_call_log with task_ref=delegation_id for cross-DB gateway detail */}
                Gateway ledger join deferred (P1 TODO).
              </p>
            )}
          </div>

          {/* Resource window (ADR-051 D3) */}
          <ResourceWindowPanel resourceWindow={trace.resource_window ?? null} />

          {/* Telemetry span tree when the new reader supplied one; the legacy
              call timeline remains the fallback for older response bodies. */}
          {trace.spans !== undefined ? (
            <SpanTree spans={trace.spans} />
          ) : trace.calls.length === 0 ? (
            <p className="text-sm text-gray-400 px-1">No per-call rows recorded for this delegation.</p>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100 text-xs text-gray-500 flex items-center gap-2 bg-gray-50">
                <span className="w-6 text-right">#</span>
                <span className="w-14">kind</span>
                <span className="flex-1">target</span>
                <span className="w-36 text-right">latency</span>
                <span className="w-3" />
              </div>
              <ul>
                {trace.calls.map((call, idx) => (
                  <CallRow
                    key={call.id ?? idx}
                    call={call}
                    isLongest={idx === longestCallIdx}
                    maxLatency={maxLatency}
                  />
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type OutcomeFilter = 'failures' | 'all'

export function Diagnostics() {
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('failures')
  const [windowHours, setWindowHours] = useState(24)
  const [delegations, setDelegations] = useState<DelegationEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [selected, setSelected] = useState<DelegationEvent | null>(null)

  const load = useCallback(async (filter: OutcomeFilter, wh: number) => {
    setLoading(true)
    setError(null)
    setForbidden(false)
    setSelected(null)
    const params: DelegationsParams = { window_hours: wh, limit: 100 }
    // Always fetch all outcomes; client-filter for "failures" toggle so we can
    // show both timeout + error without two backend requests.
    params.outcome = 'all'
    try {
      const res = await api.getDelegations(params)
      // Defensive: a fleet whose data-plane lacks the delegations endpoint (e.g.
      // a stale image) can return a body without a `delegations` array — never
      // let that crash the page with "undefined.filter". Treat it as empty.
      let rows = Array.isArray(res?.delegations) ? res.delegations : []
      if (filter === 'failures') {
        rows = rows.filter(d => d.outcome === 'timeout' || d.outcome === 'error')
      }
      setDelegations(rows)
    } catch (err) {
      if (err instanceof ForbiddenError) {
        setForbidden(true)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load delegations.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(outcomeFilter, windowHours)
  }, [outcomeFilter, windowHours, load])

  if (forbidden) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900">Diagnostics</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-6 text-sm text-red-700">
          <p className="font-semibold">Access denied</p>
          <p className="mt-1">The Diagnostics view requires Operator or Fleet Owner role.</p>
        </div>
      </div>
    )
  }

  // Trace drill-down view
  if (selected) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900">Diagnostics</h1>
        <TracePanel delegation={selected} onBack={() => setSelected(null)} />
      </div>
    )
  }

  // List view
  return (
    <div className="space-y-4">
      {/* ADR-161 — recording a run is a diagnostic, and this is the page operators already
          open when asking "why did that behave like that". A separate nav entry would put
          a once-in-a-while tool beside the daily ones; a page nobody can reach is worse
          still, which is what shipping the panel with no route amounted to. */}
      <div className="rounded border bg-white p-4">
        <CapturePanel />
      </div>

      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Diagnostics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Why did this time out? Drill into per-call attribution for any delegation.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load(outcomeFilter, windowHours)}
          disabled={loading}
          className="text-xs font-medium px-3 py-1.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex flex-wrap gap-3 items-center">
        {/* Outcome filter toggle */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">Show</span>
          <div className="flex gap-1">
            {(['failures', 'all'] as const).map(key => (
              <button
                key={key}
                type="button"
                onClick={() => setOutcomeFilter(key)}
                className={`h-7 px-3 rounded text-xs font-medium transition-colors ${
                  outcomeFilter === key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {key === 'failures' ? 'Timeouts + Errors' : 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* Time window */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">Window</span>
          <div className="flex gap-1">
            {([1, 6, 24, 48] as const).map(h => (
              <button
                key={h}
                type="button"
                onClick={() => setWindowHours(h)}
                className={`h-7 px-2.5 rounded text-xs font-medium transition-colors ${
                  windowHours === h
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {h}h
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && <div className="text-sm text-gray-400 animate-pulse">Loading delegations…</div>}

      {/* Delegations table */}
      {!loading && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {delegations.length === 0 ? (
            <p className="px-5 py-10 text-sm text-gray-400 text-center">
              No {outcomeFilter === 'failures' ? 'timeout or error ' : ''}delegations in the last {windowHours}h.
            </p>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 flex items-center gap-2 bg-gray-50">
                <span className="w-24">Outcome</span>
                <span className="flex-1">Profile</span>
                <span className="w-20 text-right">Duration</span>
                <span className="w-28 text-right">Started</span>
                <span className="w-4" />
              </div>
              <ul>
                {delegations.map(d => (
                  <li
                    key={d.id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelected(d)}
                  >
                    <div className="px-3 py-2 flex items-center gap-2 text-sm">
                      <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium w-24 ${outcomeClass(d.outcome)}`}>
                        {d.outcome}
                      </span>
                      <span className="flex-1 font-mono text-gray-800 truncate" title={d.profile}>
                        {d.profile}
                      </span>
                      <span className="shrink-0 w-20 text-right text-xs font-mono text-gray-600">
                        {fmtDuration(d.duration_s)}
                      </span>
                      <span
                        className="shrink-0 w-28 text-right text-xs text-gray-400"
                        title={new Date(d.created_at).toLocaleString()}
                      >
                        {relativeTime(d.created_at)}
                      </span>
                      <span className="shrink-0 text-gray-300 w-4 text-xs">▸</span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {!loading && delegations.length > 0 && (
        <p className="text-xs text-gray-400">
          Showing {delegations.length} {outcomeFilter === 'failures' ? 'failed ' : ''}
          delegations in the last {windowHours}h. Click a row to see the call trace.
        </p>
      )}
    </div>
  )
}
